"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import HoverArrow from "@/components/shared/HoverArrow";
import { APP_STORE_URL, PLAY_STORE_URL } from "@/components/shared/StoreButtons";

const AppleGlyph = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={cn("w-5 h-5 fill-current shrink-0", className)} aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const PlayGlyph = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={cn("w-[18px] h-[18px] fill-current shrink-0", className)} aria-hidden="true">
    <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
  </svg>
);

type Variant = "accent" | "paper";

export default function DownloadCTA({
  variant = "accent",
  className,
  label = "App kostenlos downloaden",
}: {
  variant?: Variant;
  className?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  function handleClick() {
    setOpen(true);
  }

  const btnClasses =
    variant === "accent"
      ? "bg-accent text-paper hover:bg-accent-deep"
      : "bg-paper text-accent hover:bg-paper-deep";

  return (
    <>
      <button
        onClick={handleClick}
        className={cn(
          "group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-base font-semibold min-h-[52px] whitespace-nowrap cursor-pointer transition-colors duration-200",
          btnClasses,
          className
        )}
      >
        {label}
        <HoverArrow size={16} />
      </button>

      {mounted &&
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm"
                onClick={() => setOpen(false)}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 20, scale: 0.96 }}
                  transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                  onClick={(e) => e.stopPropagation()}
                  className="relative w-full max-w-sm bg-paper rounded-2xl border border-line p-8 text-center"
                  role="dialog"
                  aria-modal="true"
                  aria-label="App herunterladen"
                >
                  <button
                    onClick={() => setOpen(false)}
                    aria-label="Schließen"
                    className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-lg text-ink-faint hover:bg-paper-deep hover:text-ink transition-colors cursor-pointer"
                  >
                    <X size={18} />
                  </button>

                  <p className="text-xs font-bold uppercase tracking-widest text-ink-faint">
                    App laden
                  </p>
                  <h3 className="font-display font-bold text-2xl text-ink mt-3 leading-[1.1]">
                    App kostenlos laden
                  </h3>
                  <p className="text-ink-soft text-sm leading-relaxed mt-2">
                    Wähle deinen Store und lade ncrechner kostenlos herunter.
                  </p>

                  <div className="mt-6 flex flex-col gap-2.5">
                    <a
                      href={APP_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 px-5 py-3 rounded-full min-h-[48px] bg-ink text-paper hover:bg-accent transition-colors duration-200 cursor-pointer"
                    >
                      <AppleGlyph />
                      <span className="font-semibold text-sm">App Store</span>
                    </a>
                    <a
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 px-5 py-3 rounded-full min-h-[48px] bg-ink text-paper hover:bg-accent transition-colors duration-200 cursor-pointer"
                    >
                      <PlayGlyph />
                      <span className="font-semibold text-sm">Google Play</span>
                    </a>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}
